import React from 'react';
import { ArrowRight, CheckCircle2, Target, Shield, Lightbulb, ArrowUpRight, BarChartIcon as ChartBarIcon, Brain, Users, Timer, LineChart, Rocket, MessageCircle, BookOpen, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTitle } from '../hooks/useTitle';
import { RoiCalculator } from '../components/RoiCalculator';
import { useEffect } from 'react';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "Quentin est un maître incontesté de l'IA. Il m'a aidé dans un mes projets à créer un business plan, coder un site internet et réaliser une étude complète de marché en un temps record. Ce qui m'aurait pris habituellement 1 mois à faire, j'ai réussi à le faire à ses côté en moins d'une demi journée. Je le recommande à 1000%. Respect !",
    author: "Benjamin Maffé",
    role: "Repreneur et Investisseur"
  },
  {
    quote: "En 4 mois, nous avons non seulement augmenté nos marges de 23%, mais aussi lancé deux nouveaux services qui représentent aujourd'hui 30% de notre croissance.",
    author: "Sophie M.",
    role: "Directrice Innovation"
  },
  {
    quote: "L'approche progressive nous a permis d'avancer sereinement. Résultat : +25% de revenus récurrents et une nouvelle ligne de business basée sur l'IA.",
    author: "Marc D.",
    role: "CEO"
  }
];

const faqs = [
  {
    question: "Combien de temps avant les premiers résultats ?",
    answer: "Premiers gains de rentabilité visibles sous 30 jours, nouvelles opportunités de croissance identifiées et activées en 60-90 jours. Nous définissons des KPIs précis dès le départ."
  },
  {
    question: "Comment garantissez-vous les résultats ?",
    answer: "Notre approche effectuale minimise les risques : chaque étape doit prouver sa valeur avant de passer à la suivante. 92% de nos clients atteignent ou dépassent leurs objectifs de rentabilité ET de croissance."
  },
  {
    question: "Je n'ai pas de compétence technique, est-ce pour moi ?",
    answer: "Absolument. Notre rôle est de faire le pont entre les possibilités de l'IA et vos enjeux business. Nous nous adaptons à votre niveau et vous accompagnons vers l'autonomie."
  },
  {
    question: "Quel investissement est nécessaire ?",
    answer: "Les projets démarrent entre 3000€ et 8000€, avec un ROI minimum visé de 3x. Nous adaptons le budget à votre capacité d'investissement (principe de perte acceptable)."
  },
  {
    question: "Que se passe-t-il après les 90 jours ?",
    answer: "Vous êtes autonome pour poursuivre votre développement avec les outils et compétences transmis. Nous pouvons également vous accompagner sur une phase d'accélération si vous le souhaitez."
  }
];

const sectors = [
  {
    title: "E-commerce (CA > 1M€)",
    rentability: "Pricing intelligent, optimisation des stocks",
    growth: "Personnalisation avancée, nouveaux services prédictifs"
  },
  {
    title: "Services B2B",
    rentability: "Automatisation intelligente, optimisation des process",
    growth: "Nouveaux services à valeur ajoutée, prédiction des besoins clients"
  },
  {
    title: "Distribution",
    rentability: "Optimisation des stocks, réduction des coûts",
    growth: "Nouveaux canaux de vente, services personnalisés"
  }
];

export function AiBoosterSprint() {
  const { setTitle } = useTitle();
  
  useEffect(() => {
    setTitle('AI Booster Sprint');
  }, [setTitle]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-20">
          <h1 className="text-4xl md:text-6xl font-bold text-center text-gray-900 mb-6 animate-slideUp">
            L'IA au service de votre croissance :<br />
            <span className="text-orange-500">+15-25% de marge</span> et nouveaux leviers de développement
          </h1>
          <p className="text-xl text-center text-gray-600 max-w-3xl mx-auto mb-12 animate-slideUp" style={{ '--delay': '0.2s' } as React.CSSProperties}>
            Une approche pragmatique pour transformer l'IA en avantage concurrentiel durable, avec des résultats mesurables dès 30 jours.
          </p>
          <div className="flex justify-center mb-12">
            <a
              href="https://tidycal.com/quentin.tousart/novster-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold flex items-center gap-2 transform hover:scale-105 transition-all animate-slideUp animate-float shadow-lg"
            >
              Réserver mon diagnostic
              <span className="px-2 py-1 bg-orange-400 rounded-full text-sm">5 places ce mois-ci</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
          <p className="text-center text-gray-700 font-medium animate-slideUp" style={{ '--delay': '0.6s' } as React.CSSProperties}>
            85% de nos clients voient une amélioration significative de leur rentabilité en 60-90 jours,<br />
            tout en développant de nouveaux axes de croissance.
          </p>
        </div>
      </div>

      {/* AI Booster Sprint Method Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">La méthode AI Booster Sprint</h2>
            <p className="text-xl text-gray-600 mb-12">
              Une approche structurée en 90 jours pour transformer l'IA en avantage concurrentiel mesurable
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-xl shadow-lg p-6 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': '0.2s' } as React.CSSProperties}>
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-4">
                  30
                </div>
                <h3 className="font-bold mb-2">Jours</h3>
                <p className="text-gray-600">
                  Premiers résultats mesurables sur votre rentabilité
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': '0.4s' } as React.CSSProperties}>
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-4">
                  60
                </div>
                <h3 className="font-bold mb-2">Jours</h3>
                <p className="text-gray-600">
                  Nouveaux leviers de croissance identifiés et activés
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': '0.6s' } as React.CSSProperties}>
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-4">
                  90
                </div>
                <h3 className="font-bold mb-2">Jours</h3>
                <p className="text-gray-600">
                  Transformation complète et avantages concurrentiels établis
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4 text-left">Objectifs clés</h3>
                  <ul className="space-y-3 text-left">
                    <li className="flex items-start gap-3">
                      <Target className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>+15-25% de marge opérationnelle</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Rocket className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>Nouveaux axes de croissance activés</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Brain className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>Autonomie sur les outils d'IA</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4 text-left">Garanties</h3>
                  <ul className="space-y-3 text-left">
                    <li className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>ROI minimum de 3x garanti</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ChartBarIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>KPIs mesurables définis dès le départ</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>Accompagnement complet de votre équipe</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Pourquoi cette approche est différente</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all transform hover:-translate-y-1"
                 style={{ '--delay': '0.2s' } as React.CSSProperties}>
              <Target className="w-12 h-12 text-orange-500 mb-6 animate-float" />
              <h3 className="text-xl font-bold mb-4">Double impact</h3>
              <p className="text-gray-600">
                Optimisation de la rentabilité immédiate + identification de nouveaux leviers de croissance
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all transform hover:-translate-y-1"
                 style={{ '--delay': '0.4s' } as React.CSSProperties}>
              <Shield className="w-12 h-12 text-orange-500 mb-6 animate-float" />
              <h3 className="text-xl font-bold mb-4">Progression maîtrisée</h3>
              <p className="text-gray-600">
                Nous avançons par petites étapes, chacune générant son propre ROI avant de passer à la suivante
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all transform hover:-translate-y-1"
                 style={{ '--delay': '0.6s' } as React.CSSProperties}>
              <Lightbulb className="w-12 h-12 text-orange-500 mb-6 animate-float" />
              <h3 className="text-xl font-bold mb-4">Vision long terme</h3>
              <p className="text-gray-600">
                Au-delà des gains rapides, nous construisons des avantages concurrentiels durables
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Un processus structuré pour maximiser votre réussite</h2>
          <div className="max-w-4xl mx-auto space-y-12">
            {[
              {
                title: "1. Diagnostic & Stratégie",
                subtitle: "Gratuit - 45 min",
                items: [
                  "Analyse de vos leviers de rentabilité actuels",
                  "Identification des opportunités de croissance",
                  "Évaluation de vos ressources existantes"
                ],
                deliverable: "Plan d'action détaillé avec ROI projeté"
              },
              {
                title: "2. Design & Planification",
                subtitle: "Semaines 1-2",
                items: [
                  "Impact rentabilité court terme",
                  "Potentiel de croissance",
                  "Rapidité d'implémentation",
                  "Ressources nécessaires",
                  "Avantage concurrentiel créé"
                ],
                deliverable: "Roadmap d'implémentation détaillée"
              },
              {
                title: "3. Implémentation",
                subtitle: "Semaines 3-8",
                items: [
                  "Mise en place des solutions priorisées",
                  "Formation continue des équipes",
                  "Mesure des résultats"
                ],
                deliverable: "KPIs hebdomadaires"
              },
              {
                title: "4. Optimisation & Scale",
                subtitle: "Semaines 9-12",
                items: [
                  "Analyse des résultats",
                  "Identification des prochaines opportunités",
                  "Préparation de la phase de croissance"
                ],
                deliverable: "ROI minimum 3x"
              }
            ].map((phase, index) => (
              <div key={phase.title} 
                   className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': `${0.2 * index}s` } as React.CSSProperties}>
                <div className="flex items-start gap-6">
                  <div className="bg-orange-100 rounded-full p-3">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                    <p className="text-orange-500 font-medium mb-4">{phase.subtitle}</p>
                    <ul className="space-y-3 mb-4">
                      {phase.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center gap-2 text-orange-500 font-medium">
                      <ArrowUpRight className="w-5 h-5" />
                      <span>Livrable : {phase.deliverable}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Effectuation Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">L'effectuation comme fondation</h2>
              <p className="text-xl text-gray-600">
                Une approche entrepreneuriale éprouvée pour maximiser les résultats tout en minimisant les risques
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': '0.2s' } as React.CSSProperties}>
                <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                  <Shield className="w-6 h-6 text-orange-500" />
                  Principe de perte acceptable
                </h3>
                <p className="text-gray-600 mb-4">
                  Nous avançons par petites étapes maîtrisées, en limitant les investissements à ce que vous pouvez vous permettre de risquer.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>ROI validé à chaque étape</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>Risque financier minimal</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': '0.4s' } as React.CSSProperties}>
                <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                  <Target className="w-6 h-6 text-orange-500" />
                  Exploitation des ressources existantes
                </h3>
                <p className="text-gray-600 mb-4">
                  Nous optimisons d'abord ce que vous avez déjà avant d'investir dans de nouvelles solutions.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>Valorisation des actifs actuels</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>Optimisation avant expansion</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': '0.6s' } as React.CSSProperties}>
                <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                  <Users className="w-6 h-6 text-orange-500" />
                  Partenariats stratégiques
                </h3>
                <p className="text-gray-600 mb-4">
                  Nous créons des alliances pour accélérer votre développement et partager les risques.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>Mutualisation des ressources</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>Accès à de nouveaux marchés</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': '0.8s' } as React.CSSProperties}>
                <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                  <Lightbulb className="w-6 h-6 text-orange-500" />
                  Exploitation des contingences
                </h3>
                <p className="text-gray-600 mb-4">
                  Nous transformons les imprévus en opportunités d'innovation et de croissance.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>Agilité stratégique</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span>Innovation continue</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <blockquote className="mt-12 text-center">
              <p className="text-xl text-gray-600 italic mb-4">
                "L'effectuation n'est pas juste une théorie, c'est une approche pragmatique qui a fait ses preuves dans le développement de centaines d'entreprises."
              </p>
              <footer className="text-gray-500">
                - Pr. Dominique Vian, co-auteur avec Quentin Tousart
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Qui suis-je ?</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-8 mb-8">
                <img
                  src="https://media.licdn.com/dms/image/v2/D4E03AQEZXx_XgYGeNQ/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1724314192598?e=1743033600&v=beta&t=aEJJvyZGvjQ1n-sKdr4h-JYAVSLRY5JREYurKzM_B5U"
                  alt="Quentin Tousart"
                  className="w-32 h-32 rounded-full object-cover"
                />
                <div>
                  <h3 className="text-2xl font-bold mb-2">Quentin Tousart</h3>
                  <p className="text-gray-600 mb-4">
                    Mon parcours entrepreneurial m'a appris l'importance d'allier vision long terme et résultats concrets.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Rocket className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Co-fondateur de Webdistrib.com</h4>
                    <p className="text-gray-600">Revendu avec succès</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Brain className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Projet open source concurrent de PrestaShop</h4>
                    <p className="text-gray-600">Un échec qui m'a appris l'importance d'une approche progressive</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <BookOpen className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Co-auteur avec le Professeur Dominique Vian</h4>
                    <p className="text-gray-600">Livre sur l'effectuation</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Aujourd'hui</h4>
                    <p className="text-gray-600">Je dirige une équipe de 5 experts dédiés à la transformation d'entreprises par l'IA</p>
                  </div>
                </div>
              </div>
              <p className="mt-8 text-gray-700 italic border-l-4 border-orange-500 pl-4">
                Cette expérience m'a appris une chose essentielle : le succès vient d'une approche qui combine gains rapides et construction méthodique d'avantages durables.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Pour qui ?</h2>
          
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold mb-6">Idéal pour les entreprises qui veulent :</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>PME et ETI réalisant entre 1M€ et 50M€ de CA</span>
                </li>
                <li className="flex items-start gap-3">
                  <LineChart className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Optimiser leur rentabilité tout en préparant leur croissance future</span>
                </li>
                <li className="flex items-start gap-3">
                  <Rocket className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Se différencier durablement de la concurrence</span>
                </li>
                <li className="flex items-start gap-3">
                  <Timer className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>Voir des résultats concrets en 90 jours</span>
                </li>
              </ul>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-center mb-8">Exemples concrets par secteur</h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {sectors.map((sector, index) => (
              <div key={sector.title}
                   className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all"
                   style={{ '--delay': `${0.2 * index}s` } as React.CSSProperties}>
                <h4 className="text-xl font-bold mb-4">{sector.title}</h4>
                <div className="space-y-4">
                  <div>
                    <h5 className="font-semibold text-orange-500 mb-2">Rentabilité</h5>
                    <p className="text-gray-600">{sector.rentability}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-orange-500 mb-2">Croissance</h5>
                    <p className="text-gray-600">{sector.growth}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Témoignages vérifiés</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 animate-slideUp hover:shadow-xl transition-all"
                style={{ '--delay': `${0.2 * index}s` } as React.CSSProperties}
              >
                <MessageCircle className="w-12 h-12 text-orange-500 mb-6" />
                <blockquote className="text-gray-700 mb-6">
                  {testimonial.quote}
                </blockquote>
                <div>
                  <div className="font-bold">{testimonial.author}</div>
                  <div className="text-orange-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Questions fréquentes</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all"
              >
                <h3 className="text-xl font-bold mb-4">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Découvrez votre potentiel</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Utilisez notre calculateur de ROI pour estimer vos gains potentiels
          </p>
          <Link
            to="/calculateur-roi"
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-orange-600 transition-colors"
          >
            Calculer mon ROI
            <Calculator className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">Prêt à transformer votre business ?</h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto">
            Réservez votre diagnostic gratuit et découvrez comment l'IA peut devenir votre avantage concurrentiel
          </p>
          <a
            href="https://tidycal.com/quentin.tousart/novster-ai"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-orange-500 px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-gray-100 transform hover:scale-105 transition-all"
          >
            Réserver mon diagnostic
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}